import { Upload, FileText, X } from "lucide-react";
import { useRef, useState, useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";

const ACCEPTED_MIME = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);

function isAcceptedFile(file: File): boolean {
  if (file.type && ACCEPTED_MIME.has(file.type)) {
    return true;
  }
  const lower = file.name.toLowerCase();
  return lower.endsWith(".pdf") || lower.endsWith(".doc") || lower.endsWith(".docx");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

type FileUploadProps = {
  readonly label: string;
  readonly accept?: string;
  readonly maxSizeMB?: number;
  readonly value: File | null;
  readonly onChange: (file: File | null) => void;
  readonly error?: string;
  readonly required?: boolean;
  readonly helperText?: string;
  readonly className?: string;
};

export const FileUpload = ({
  label,
  accept = ".pdf,.doc,.docx",
  maxSizeMB = 10,
  value,
  onChange,
  error,
  required = false,
  helperText,
  className,
}: FileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = error ?? localError;

  useEffect(() => {
    if (value !== null) {
      setLocalError(null);
    }
  }, [value]);

  const handleFile = useCallback(
    (file: File) => {
      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        setLocalError(`File must be ${maxSizeMB}MB or smaller.`);
        onChange(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      if (!isAcceptedFile(file)) {
        setLocalError("Please upload a Word file (.doc, .docx).");
        onChange(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      setLocalError(null);
      onChange(file);
    },
    [maxSizeMB, onChange],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      if (file) {
        handleFile(file);
      }
    },
    [handleFile],
  );

  const handleRemove = useCallback(() => {
    setLocalError(null);
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [onChange]);

  const fieldId = `file-upload-${label.toLowerCase().replaceAll(/\s+/g, "-")}`;
  const labelId = `${fieldId}-label`;

  return (
    <div className={twMerge("space-y-1.5", className)}>
      <div id={labelId} className="form-label">
        {label}
        {required && <span className="form-label-asterisk">*</span>}
      </div>

      {value ? (
        <div className="file-summary-row" aria-labelledby={labelId}>
          <div className="file-summary-row__icon" aria-hidden>
            <FileText className="h-5 w-5" />
          </div>
          <div className="file-summary-row__meta">
            <p className="file-summary-row__name">{value.name}</p>
            <p className="file-summary-row__size">{formatFileSize(value.size)}</p>
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="file-summary-row__remove"
            aria-label="Remove file"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-labelledby={labelId}
          onClick={() => inputRef.current?.click()}
          className={twMerge("file-dropzone", displayError && "file-dropzone--error")}
        >
          <div className="file-dropzone__icon-wrap">
            <Upload className="h-6 w-6" strokeWidth={1.5} aria-hidden />
          </div>
          <p className="file-dropzone__primary">Click to upload your document.</p>
          <p className="file-dropzone__secondary">
            {helperText ?? `Word document (Max ${maxSizeMB}MB)`}
          </p>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        tabIndex={-1}
      />

      {displayError && <p className="text-xs text-red-500">{displayError}</p>}
    </div>
  );
};
